/** Ported verbatim from templates/add_scraper.html's TEMPLATE_FIELDS.
 *  Field ids are the exact keys the backend templates read out of config_json,
 *  so they must not be renamed. */

export interface TemplateField {
  id: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  type?: 'checkbox';
  help: string;
}

export type TemplateId = 'runfair' | 'shopify' | 'hypecommerce' | 'eql' | 'guided' | 'custom';

export const TEMPLATES: Array<{ id: TemplateId; label: string; blurb: string }> = [
  { id: 'runfair', label: 'RunFair', blurb: 'Nike AU, INCU, LoFi, Fast Times, Pokebox' },
  { id: 'shopify', label: 'Shopify', blurb: 'Finesse, Above The Clouds, any Shopify store' },
  { id: 'hypecommerce', label: 'HypeCommerce', blurb: 'Up There Store' },
  { id: 'eql', label: 'EQL', blurb: 'EQL ballot platform' },
  { id: 'guided', label: 'Guided builder', blurb: 'Point at a page and pick selectors' },
  { id: 'custom', label: 'Custom Python', blurb: 'Write the scraper yourself' },
];

export const TEMPLATE_FIELDS: Record<TemplateId, TemplateField[]> = {
  runfair: [
    { id: 'base_url', label: 'Base URL', placeholder: 'https://nikeau.runfair.com', required: true, help: 'The RunFair store URL. Must have a /page-data/ JSON endpoint.' },
    { id: 'locale', label: 'Locale', placeholder: 'en-GB/au', help: 'Locale path segment for the page-data endpoint. Default: en-GB/au' },
    { id: 'draws_path', label: 'Draws Path', placeholder: 'result.pageContext.draws', help: 'Dot-separated path to the draws array in the JSON response.' },
  ],
  shopify: [
    { id: 'base_url', label: 'Base URL', placeholder: 'https://www.finessestore.com', required: true, help: 'The Shopify store URL (homepage).' },
    { id: 'collections_path', label: 'Collections Path', placeholder: '/collections/launches', help: 'Path to the launches/raffles collection page.' },
    { id: 'use_cloudscraper', label: 'Use Cloudscraper (for CF-protected sites)', type: 'checkbox', help: 'Enable if the site uses Cloudflare protection.' },
  ],
  hypecommerce: [
    { id: 'base_url', label: 'Base URL', placeholder: 'https://uptherestore.com', required: true, help: 'The store URL (must use HypeCommerce platform).' },
    { id: 'launches_path', label: 'Launch Path', placeholder: '/collections/launches', help: 'Path to the launches collection page.' },
    { id: 'api_url', label: 'API URL', placeholder: 'https://admin.hypecommerce.io/api/raffles', help: 'HypeCommerce API endpoint for raffle timing data. Usually auto-detected.' },
  ],
  eql: [
    { id: 'country', label: 'Country Code', placeholder: 'AU', help: 'ISO country code to filter ballots. Default: AU' },
    { id: 'retailer', label: 'Retailer Filter', placeholder: 'e.g. Nike', help: 'Optional — only show ballots from this retailer (substring match).' },
  ],
  guided: [
    { id: 'base_url', label: 'Base URL', placeholder: 'https://example.com', required: true, help: 'The site origin or page URL used to resolve relative links and images.' },
    { id: 'page_url', label: 'Page URL', placeholder: 'https://example.com/launches', help: 'Exact page to inspect and scrape. Defaults to Base URL.' },
    { id: 'item_selector', label: 'Raffle Card Selector', placeholder: 'div.product-card', required: true, help: 'Repeated element that wraps one raffle/product card.' },
    { id: 'title_selector', label: 'Title Selector', placeholder: 'h2, .product-title', help: 'Selector inside each card for the raffle/product name.' },
    { id: 'link_selector', label: 'Link Selector', placeholder: 'a', help: 'Selector inside each card for the entry/product link.' },
    { id: 'image_selector', label: 'Image Selector', placeholder: 'img', help: 'Selector inside each card for the product image.' },
    { id: 'price_selector', label: 'Price Selector', placeholder: '.price', help: 'Optional selector inside each card for price.' },
    { id: 'close_selector', label: 'Close Date Selector', placeholder: '.close-date', help: 'Optional selector containing close/end text.' },
    { id: 'fetch_detail_pages', label: 'Fetch each detail page before extracting fields', type: 'checkbox', help: 'Use when the listing page only has links and the real info is on each detail page.' },
    { id: 'auto_detail_extract', label: 'Auto extract detail info from page text/metadata', type: 'checkbox', help: 'Use common page metadata and text patterns when exact detail selectors are unknown.' },
    { id: 'dedupe_links', label: 'Only process each detail link once', type: 'checkbox', help: 'Prevents broad link selectors from spending the run on duplicate URLs.' },
    { id: 'include_text', label: 'Only include text containing', placeholder: 'raffle', help: 'Optional text filter applied before fetching details.' },
    { id: 'exclude_text', label: 'Exclude text containing', placeholder: 'entries closed', help: 'Optional text filter for closed/noisy listing links.' },
    { id: 'include_url_pattern', label: 'Only include URLs matching', placeholder: '/raffles/', help: 'Optional regular expression applied to each detail URL.' },
    { id: 'exclude_url_pattern', label: 'Exclude URLs matching', placeholder: 'terms|login|register', help: 'Optional regular expression for noisy links.' },
    { id: 'detail_content_selector', label: 'Detail Content Selector', placeholder: 'main, article', help: 'Optional wrapper on the detail page. Blank searches the whole page.' },
    { id: 'detail_title_selector', label: 'Detail Title Selector', placeholder: 'h1', help: 'Title selector on each detail page. Defaults to the listing title selector.' },
    { id: 'detail_image_selector', label: 'Detail Image Selector', placeholder: 'img, .hero img', help: 'Main image on each detail page. Falls back to og:image.' },
    { id: 'detail_price_selector', label: 'Detail Price Selector', placeholder: '.price', help: 'Optional price selector on each detail page.' },
    { id: 'detail_close_selector', label: 'Detail Close Date Selector', placeholder: '.close-date, time', help: 'Selector on each detail page containing close/end text.' },
    { id: 'detail_delay_seconds', label: 'Detail Page Delay (seconds)', placeholder: '0.5', help: 'Small pause between detail-page requests.' },
    { id: 'max_items', label: 'Max Items', placeholder: '20', help: 'Caps how many cards are parsed each run.' },
    { id: 'fallback_cutoff_hours', label: 'Fallback Close Window (hours)', placeholder: '168', help: 'Used when no close date is found. Default is 7 days.' },
  ],
  custom: [],
};

export const INTERVALS = [
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
  { value: 600, label: '10 minutes' },
  { value: 1800, label: '30 minutes' },
  { value: 3600, label: '1 hour' },
];

export const DEFAULT_CUSTOM_CODE = `"""Custom scraper. Return a list of raffle dicts from scrape()."""
from raffle_sites.http_client import make_session, safe_get

SITE_NAME = "My Store"


def scrape(config):
    session = make_session(use_curl_cffi=True)
    resp = safe_get(session, "https://example.com/raffles")
    raffles = []
    # Append dicts shaped like:
    # {
    #   "product": "Air Jordan 1",
    #   "link": "https://example.com/raffles/aj1",
    #   "picture_url": "https://example.com/aj1.jpg",
    #   "price": "250",
    #   "subscription_cutoff_time": 1780000000,   # unix seconds
    #   "collection_methods": ["Online draw"],
    #   "sku": "N/A",
    #   "start": "N/A",
    # }
    return raffles
`;
